import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Search, Menu, X, FileText, ArrowRight, Globe, Check } from 'lucide-react'
import { clsx } from 'clsx'
import Fuse from 'fuse.js'
import { flattenNavigation } from '@/lib/navigation'
import { useLocale, LOCALES, LOCALE_LABELS, Locale, TranslationKey } from '@/i18n'

interface HeaderProps {
  onMenuClick?: () => void
  isSidebarOpen?: boolean
}

export function Header({ onMenuClick, isSidebarOpen }: HeaderProps) {
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const location = useLocation()
  const { t, localizeHref, locale } = useLocale()

  const navItems: { labelKey: TranslationKey; href: string }[] = useMemo(() => ([
    { labelKey: 'header.nav.vision', href: localizeHref('/faq/vision-product-market/what-is-rexhook') },
    { labelKey: 'header.nav.technical', href: localizeHref('/faq/technical/v4-hooks') },
    { labelKey: 'header.nav.team', href: localizeHref('/faq/team-trust/overview') },
    { labelKey: 'header.nav.revenue', href: localizeHref('/faq/revenue/how-makes-money') },
    { labelKey: 'header.nav.roadmap', href: localizeHref('/faq/participation/go-to-market') },
  ]), [localizeHref])

  // Close mobile nav when route changes
  useEffect(() => {
    setIsMobileNavOpen(false)
  }, [location.pathname])

  // Prevent body scroll when mobile nav is open
  useEffect(() => {
    if (isMobileNavOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [isMobileNavOpen])

  const isActiveNav = (href: string) => {
    const parts = href.split('/')
    const section = parts.slice(0, locale === 'en' ? 4 : 5).join('/')
    return location.pathname.startsWith(section.replace(/\/[^/]+$/, ''))
  }

  // Keyboard shortcuts for search
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        const tag = (e.target as HTMLElement).tagName
        if (tag !== 'INPUT' && tag !== 'TEXTAREA') {
          e.preventDefault()
          setIsSearchOpen(true)
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setIsSearchOpen(prev => !prev)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <>
      <header className="header">
        <div className="max-w-[1440px] mx-auto h-full px-4 flex items-center justify-between gap-4">
          {/* Logo and Nav Links */}
          <div className="flex items-center gap-8">
            {/* Logo */}
            <Link to={localizeHref('/')} className="flex items-center gap-2 flex-shrink-0">
              <img
                src="/icon.svg"
                alt="RexHook"
                className="w-6 h-6 md:w-7 md:h-7"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
              <span className="font-semibold text-white text-base md:text-lg">{t('header.brand')}</span>
            </Link>

            {/* Nav Links */}
            <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={clsx(
                    'px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                    isActiveNav(item.href)
                      ? 'text-white'
                      : 'text-[#71717A] hover:text-[#A1A1AA] hover:bg-white/[0.04]'
                  )}
                >
                  {t(item.labelKey)}
                </Link>
              ))}
            </nav>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Search Bar */}
            <div className="hidden md:flex w-80">
              <button
                onClick={() => setIsSearchOpen(true)}
                className="w-full flex items-center gap-3 px-4 py-2 bg-white/[0.04] border border-white/[0.06] rounded-xl text-sm text-[#71717A] hover:border-white/[0.1] transition-colors h-10"
              >
                <Search className="w-4 h-4" />
                <span>{t('header.searchPlaceholder')}</span>
                <kbd className="ml-auto text-[10px] bg-white/[0.06] px-1.5 py-0.5 rounded border border-white/[0.06]">/</kbd>
              </button>
            </div>
            {/* Mobile search button */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="md:hidden p-2 text-[#71717A] hover:text-white hover:bg-white/[0.04] rounded-lg transition-colors"
              aria-label={t('header.searchAria')}
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Language switcher */}
            <LanguageSwitcher />

            {/* Mobile menu button */}
            <button
              onClick={() => onMenuClick ? onMenuClick() : setIsMobileNavOpen(!isMobileNavOpen)}
              className="lg:hidden p-2.5 text-[#71717A] hover:text-white hover:bg-white/[0.04] rounded-lg transition-colors"
              aria-label={(isSidebarOpen || isMobileNavOpen) ? t('header.closeMenu') : t('header.openMenu')}
            >
              {(isSidebarOpen || isMobileNavOpen) ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Nav Overlay - Only show when no sidebar handler (homepage) */}
      {!onMenuClick && (
        <>
          <div
            className={clsx(
              'fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden transition-opacity',
              isMobileNavOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            )}
            onClick={() => setIsMobileNavOpen(false)}
          />
          <div
            className={clsx(
              'fixed top-14 left-0 right-0 z-50 bg-[#0a0a0c] border-b border-white/[0.06] lg:hidden transition-all',
              isMobileNavOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
            )}
          >
            <nav className="max-w-[1440px] mx-auto px-4 py-4 flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={clsx(
                    'px-4 py-3 text-base font-medium rounded-xl transition-colors',
                    isActiveNav(item.href)
                      ? 'text-white bg-white/[0.06]'
                      : 'text-[#A1A1AA] hover:text-white hover:bg-white/[0.04]'
                  )}
                >
                  {t(item.labelKey)}
                </Link>
              ))}
              <Link
                to={localizeHref('/faq/vision-product-market/what-is-rexhook')}
                className="mt-2 px-4 py-3 text-base font-medium text-[#FC87B7] bg-[#FC87B7]/10 rounded-xl text-center"
              >
                {t('header.getStarted')}
              </Link>
            </nav>
          </div>
        </>
      )}

      {/* Search Modal */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  )
}

/* ─── Language Switcher ──────────────────────────── */

function LanguageSwitcher() {
  const [open, setOpen] = useState(false)
  const { locale, switchLocale, t } = useLocale()

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-lang-switcher]')) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('click', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="relative" data-lang-switcher>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 p-2 text-[#71717A] hover:text-white hover:bg-white/[0.04] rounded-lg transition-colors"
        aria-label={t('header.language')}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Globe className="w-4 h-4" />
        <span className="text-[12px] font-medium uppercase tracking-wider">{locale}</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-40 bg-[#0f0f12] border border-white/[0.08] rounded-xl shadow-2xl py-1 z-50"
        >
          {LOCALES.map(l => (
            <button
              key={l}
              role="menuitemradio"
              aria-checked={l === locale}
              onClick={() => { switchLocale(l as Locale); setOpen(false) }}
              className={clsx(
                'w-full flex items-center justify-between gap-2 px-3 py-2 text-[13px] text-left transition-colors',
                l === locale ? 'text-white bg-white/[0.04]' : 'text-white/60 hover:text-white hover:bg-white/[0.04]'
              )}
            >
              <span>{LOCALE_LABELS[l as Locale]}</span>
              {l === locale && <Check className="w-3.5 h-3.5 text-[#FC87B7]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/* ─── Search Modal ──────────────────────────────── */

function SearchModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('')
  const { locale, t } = useLocale()

  const searchItems = useMemo(() => flattenNavigation(locale), [locale])
  const fuse = useMemo(
    () => new Fuse(searchItems, { keys: ['title', 'section'], threshold: 0.4, includeScore: true }),
    [searchItems]
  )

  const [results, setResults] = useState<typeof searchItems>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const navigate = useNavigate()

  useEffect(() => {
    if (query.trim()) {
      const searchResults = fuse.search(query).map(result => result.item)
      setResults(searchResults.slice(0, 8))
      setSelectedIndex(0)
    } else {
      setResults(searchItems.slice(0, 8))
      setSelectedIndex(0)
    }
  }, [query, fuse, searchItems])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex(prev => (prev + 1) % results.length)
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex(prev => (prev - 1 + results.length) % results.length)
          break
        case 'Enter':
          e.preventDefault()
          if (results[selectedIndex]) {
            navigate(results[selectedIndex].href)
            onClose()
            setQuery('')
          }
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          setQuery('')
          break
      }
    },
    [isOpen, results, selectedIndex, navigate, onClose]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={() => { onClose(); setQuery('') }}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-[#0f0f12] border border-white/[0.08] rounded-2xl shadow-2xl overflow-hidden">
        {/* Search input */}
        <div className="flex items-center gap-3 px-5 border-b border-white/[0.06]">
          <Search className="w-5 h-5 text-white/30" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('search.placeholder')}
            className="flex-1 py-4 bg-transparent text-white placeholder:text-white/30 focus:outline-none text-base"
            autoFocus
          />
          <button
            onClick={() => { onClose(); setQuery('') }}
            className="p-1.5 rounded-lg hover:bg-white/[0.06] text-white/30 hover:text-white/60 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[400px] overflow-y-auto py-2">
          {results.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <p className="text-white/30">{t('search.noResults')} "{query}"</p>
            </div>
          ) : (
            results.map((item, index) => (
              <button
                key={item.href}
                onClick={() => {
                  navigate(item.href)
                  onClose()
                  setQuery('')
                }}
                onMouseEnter={() => setSelectedIndex(index)}
                className={clsx(
                  'w-full flex items-center gap-4 px-5 py-3 text-left transition-colors',
                  index === selectedIndex
                    ? 'bg-white/[0.06] text-white'
                    : 'text-white/60 hover:text-white'
                )}
              >
                <FileText className={clsx(
                  'w-5 h-5 flex-shrink-0',
                  index === selectedIndex ? 'text-[#FC87B7]' : 'text-white/30'
                )} />
                <div className="min-w-0 flex-1">
                  <div className="truncate">{item.title}</div>
                  {item.section && (
                    <div className="text-[12px] text-white/30 mt-0.5 truncate">{item.section}</div>
                  )}
                </div>
                {index === selectedIndex && (
                  <ArrowRight className="w-4 h-4 text-[#FC87B7]" />
                )}
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/[0.06] bg-white/[0.02] flex items-center gap-6 text-xs text-white/30">
          <span className="flex items-center gap-1.5">
            <kbd className="px-2 py-1 bg-white/[0.06] rounded border border-white/[0.06] font-mono">↵</kbd>
            <span>{t('search.selectHint')}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="px-2 py-1 bg-white/[0.06] rounded border border-white/[0.06] font-mono">↑</kbd>
            <kbd className="px-2 py-1 bg-white/[0.06] rounded border border-white/[0.06] font-mono">↓</kbd>
            <span>{t('search.navigateHint')}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="px-2 py-1 bg-white/[0.06] rounded border border-white/[0.06] font-mono">esc</kbd>
            <span>{t('search.closeHint')}</span>
          </span>
        </div>
      </div>
    </div>
  )
}
